import { useIntersectionObserver } from '@vueuse/core';
import { Ref, computed, watch } from 'vue';
import { useState } from 'vuex-composition-helpers';
import { NAMESPACE } from '@ve/components/hotel/filters/common/store/hotelPageFiltersAdapterStore';
import { MapForFiltersState } from '@ve/components/hotel/filters/common/store/types';
import useLeadBannerTracking from './useLeadBannerTracking';
import type { ContextInput } from './useLeadBannerTracking';
import {
    LEAD_BANNER_PLACEMENT,
    LEAD_BANNER_TRIGGER_REASON,
} from './analytics/types';

interface UseHotelLeadBannerTrackingOptions {
    formRef: Ref<{ $el?: Element } | null>;
    isSubmitted: Ref<boolean>;
}

interface UseHotelLeadBannerTrackingReturn {
    onFormStart: () => void;
    onSheetOpen: () => void;
}

const HOTEL_CONTEXT: ContextInput = {
    contextKey: 'hotel_page',
    placement: LEAD_BANNER_PLACEMENT.HOTEL_PAGE,
    position: null,
    triggerReason: LEAD_BANNER_TRIGGER_REASON.DEFAULT,
};

export default function useHotelLeadBannerTracking(
    options: UseHotelLeadBannerTrackingOptions,
): UseHotelLeadBannerTrackingReturn {
    const tracking = useLeadBannerTracking();

    const { loadingState } = useState<MapForFiltersState>(NAMESPACE, ['loadingState']);

    // Импрешн отправляем по факту попадания DOM-узла баннера в viewport,
    // а не по готовности данных — иначе событие срабатывает, пока пользователь
    // ещё в верху страницы и баннера не видит.
    const targetEl = computed(
        () => (options.formRef.value?.$el ?? undefined) as HTMLElement | undefined,
    );
    useIntersectionObserver(targetEl, ([entry]) => {
        if (entry?.isIntersecting) {
            tracking.onImpression(HOTEL_CONTEXT);
        }
    });

    watch(options.isSubmitted, (next, prev) => {
        if (next && !prev) {
            tracking.onFormSubmit(HOTEL_CONTEXT);
        }
    });

    watch(loadingState, (next) => {
        if (next === 'loading') {
            tracking.resetContexts();
        }
    });

    return {
        onFormStart: () => tracking.onFormStart(HOTEL_CONTEXT),
        onSheetOpen: () => tracking.onSheetOpen(HOTEL_CONTEXT),
    };
}
